import { markParentReferralAuthenticationComplete } from "#/src/utils/parentReferralIntent";
import { setEntityId, setEntityType } from "@classdojo/log-client";
import { paths } from "@web-monorepo/api-types";
import { AxiosError } from "axios";
import useMutation from "#/src/hooks/useMutation";
import { monolithApiClient } from "#/src/configs/axios.config";
import { setSessionQueryData } from "#/src/hooks/useSession";
import { logTutorEvent } from "#/src/utils/log";
import useHelperWidgetAPI from "#/src/hooks/useHelperWidgetAPI";
import { setDefaultTimezone } from "#/src/utils/dates";
import { localStorage } from "@web-monorepo/safe-browser-storage";

type Endpoint = paths["/api/marketplace/login"]["post"];
type Body = Endpoint["requestBody"]["content"]["application/json"];
type Response = Endpoint["responses"][200]["content"]["application/json"];
export type AuthErrorResponse = Endpoint["responses"][401];

// `personProfileId` is returned by the marketplace `/session` + `/login` endpoints (the caller's
// chat identity, used as the messaging `entityId`). Augmented locally until `@web-monorepo/api-types`
// is regenerated to include it; the field is optional since not every user has a PersonProfile.
export type SessionData = Response & { personProfileId?: string };

export const getEntityType = (roles: string[]) => (roles.includes("teacher") ? "teacher" : "parent");

const login = async (body: Body) => {
  const response = await monolithApiClient.post<Response>(`/login`, body);
  markParentReferralAuthenticationComplete(response.data.user_details.uid);
  setSessionQueryData(response.data);
  return response;
};
const useLoginMutation = () => {
  const helperWidgetAPI = useHelperWidgetAPI();

  return useMutation(login, {
    onSuccess: (data: { data: Response }) => {
      // Clear booking localStorage on login to prevent 403s from stale cross-user data
      localStorage.removeItem("booking");

      const uid = data.data.user_details.uid;
      const userType = getEntityType(data.data.roles);
      setEntityId(uid);
      setEntityType(userType);
      helperWidgetAPI.setWidgetUserData({
        userId: uid,
        userType,
        email: data.data.user_details.email,
        fullname: `${data.data.user_details.first_name ?? ""} ${data.data.user_details.last_name ?? ""}`,
        caller: "useLoginMutation",
      });
      setDefaultTimezone(data.data.user_details.timezone);
      logTutorEvent({ eventName: "marketplace.login.success" });
    },
    onError: (error: AxiosError) => {
      logTutorEvent({ eventName: "marketplace.login.failure", metadata: { code: error.code, message: error.message } });
    },
  });
};

export default useLoginMutation;
